import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { me, changeUserPassword } from "@/lib/api/auth.functions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/profile/")({
  loader: () => me(),
  component: AdminProfilePage,
});

function AdminProfilePage() {
  const user = Route.useLoaderData();
  if (!user) throw new Error("Not authenticated");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleChangePassword() {
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    setSaving(true);
    try {
      const result = await changeUserPassword({
        data: { currentPassword, newPassword },
      });
      if (!result.ok) {
        toast.error(result.error ?? "Failed to change password");
        return;
      }
      toast.success("Password updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to change password");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Your profile" />

      <section className="space-y-4 rounded-lg border p-6 max-w-lg">
        <h2 className="text-lg font-semibold">Account</h2>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-muted-foreground">Name</p>
            <p className="font-medium">{user.name}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Email</p>
            <p className="font-medium">{user.email}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Role</p>
            <p className="font-medium capitalize">{user.roleName.replace(/_/g, " ")}</p>
          </div>
        </div>
      </section>

      <section className="space-y-4 rounded-lg border p-6 max-w-lg">
        <h2 className="text-lg font-semibold">Change password</h2>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <Button
            type="button"
            onClick={() => void handleChangePassword()}
            disabled={saving || !currentPassword || !newPassword || !confirmPassword}
          >
            {saving ? "Updating…" : "Update password"}
          </Button>
        </div>
      </section>
    </AdminPageShell>
  );
}
